// ---------------------------------------------------------------------------
// MKT-025 Phase 2 — Marketplace Puck Components Schema
// ---------------------------------------------------------------------------
// Story MKT-025: "Custom Puck component marketplace supports package, review,
// install and sandbox/security validation."
//
// Phase 1 (LANDED 2026-05-13) shipped the pure-function classifier in
// `packages/marketing/src/compliance/mkt-025-puck-component-review-classifier.ts`
// with closed `MARKETPLACE_COMPONENT_VERDICTS`, `MARKETPLACE_COMPONENT_RULES`,
// `ALLOWED_LICENSES`, and `MAX_BUNDLE_BYTES = 256 * 1024`.
//
// Phase 2 (this file) ships the persistence schema. The status column reuses
// the Phase 1 verdict vocabulary verbatim so the submit route can persist
// `classifyMarketplaceComponent(...).verdict` directly. The two surfaces are
// kept in sync by `MARKETPLACE_COMPONENT_STATUSES` here mirroring the verdict
// catalog; the schema-pin test enforces the two stay 1:1.
//
// Phase 3 (route — `POST /v1/marketplace/components/submit` + admin review),
// Phase 4 (sandbox host architecture decision — V8 isolate vs iframe vs Web
// Worker vs CF-Sandbox), and Phase 5 (`component.marketplace_*@v1` CQRS
// events) land separately. Phase 4 is the external blocker on the install/
// execution half; this PR ships only the schema half so it can land
// independently per the registry note.
//
// Design rules locked here:
// - One row per `(tenant_id, listing_key)` where `listing_key` is a stable
//   hash of `tenantId + author_publisher + listing_name + version`. The
//   unique index enforces submit-idempotency.
// - `marketplace_components.status` mirrors `MARKETPLACE_COMPONENT_VERDICTS`
//   verbatim. Stored as text for forward-compat; validation runs at the route
//   boundary, not the DB layer, so future verdict additions need zero
//   migration.
// - `license` is one of `MARKETPLACE_COMPONENT_LICENSE_VOCAB` (SPDX subset
//   matching Phase 1 `ALLOWED_LICENSES`). The route layer rejects unknown
//   SPDX ids before write via the Phase 1 `missing_license` rule.
// - `bundle_bytes` mirrors the Phase 1 `MAX_BUNDLE_BYTES` upper-bound (256
//   KiB); column type `integer` is comfortable (max signed int = 2 GiB) and
//   the Phase 1 `oversize_bundle` rule rejects > 256 KiB before write.
// - `external_script_hosts` is `text[]` of allow-listed origins captured at
//   submit time; the Phase 4 sandbox host enforces this allow-list at
//   runtime when the component finally executes.
// - `blocked_rules` + `flagged_rules` are `text[]` of Phase 1 rule codes for
//   downstream UI/admin filtering.
// - GDPR posture: marketplace listings are cross-tenant by design. The table
//   stores component metadata + signed publisher id (not personal data per
//   se), so it stays outside the platform PII surface. Erasure cascades by
//   `tenant_id` alongside the rest of the marketplace lineage.
// ---------------------------------------------------------------------------

import {
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

/**
 * Closed vocabulary mirroring `MARKETPLACE_COMPONENT_VERDICTS` from
 * `@ease/marketing/compliance` Phase 1. Re-declared here as a static tuple so
 * `@ease/db` does not depend on `@ease/marketing`; the schema-pin test
 * enforces these two lists stay 1:1.
 */
export const MARKETPLACE_COMPONENT_STATUSES = [
  "auto_approve",
  "needs_review",
  "rejected",
] as const;

export type MarketplaceComponentStatus =
  (typeof MARKETPLACE_COMPONENT_STATUSES)[number];

export function isMarketplaceComponentStatus(
  value: unknown,
): value is MarketplaceComponentStatus {
  return (
    typeof value === "string" &&
    (MARKETPLACE_COMPONENT_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Closed SPDX-id vocabulary mirroring `ALLOWED_LICENSES` from Phase 1.
 * Schema-pin test enforces 1:1 with the classifier.
 */
export const MARKETPLACE_COMPONENT_LICENSE_VOCAB = [
  "MIT",
  "Apache-2.0",
  "BSD-2-Clause",
  "BSD-3-Clause",
  "ISC",
  "MPL-2.0",
] as const;

export type MarketplaceComponentLicenseVocab =
  (typeof MARKETPLACE_COMPONENT_LICENSE_VOCAB)[number];

/**
 * Phase 1 bundle-size cap mirrored here for the schema-pin test. The route
 * layer enforces the cap via the Phase 1 `oversize_bundle` rule BEFORE
 * write; this constant is the column's documentation, not a CHECK.
 */
export const MARKETPLACE_COMPONENT_MAX_BUNDLE_BYTES = 256 * 1024;

export const marketplaceComponents = pgTable(
  "marketplace_components",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Stable hash over (tenantId, author_publisher, listing_name, version).
     * Re-submitting the same component version updates `status`/
     * `blocked_rules`/`flagged_rules` rather than creating a duplicate row.
     */
    listingKey: text("listing_key").notNull(),
    /** Display name; classifier enforces metadata validity. */
    listingName: text("listing_name").notNull(),
    /** Semver-like author-supplied version string; classifier validates. */
    version: text("version").notNull(),
    /**
     * Signed-publisher id. When NULL, the Phase 1 `unverified_author`
     * soft-flag rule fires unless `policy.allowUnsignedAuthors` is set on
     * the tenant.
     */
    authorPublisher: text("author_publisher"),
    /** One of `MARKETPLACE_COMPONENT_LICENSE_VOCAB` (SPDX subset). */
    license: text("license").notNull(),
    /**
     * Bundle size in bytes. Phase 1 rule `oversize_bundle` rejects > 256
     * KiB; the column itself is uncapped so future tenant overrides
     * (`policy.allowOversizeBundles`) can persist larger bundles without a
     * migration.
     */
    bundleBytes: integer("bundle_bytes").notNull(),
    /**
     * Optional bundle storage URL (R2 object key, blob, etc.). Phase 3
     * defines the storage handoff; schema leaves it free-form.
     */
    bundleUrl: text("bundle_url"),
    /**
     * Allow-listed external script hosts captured at submit. Phase 1 rule
     * `external_script_src` rejects script srcs to hosts NOT in this list.
     * Phase 4 sandbox host enforces the list at runtime.
     */
    externalScriptHosts: text("external_script_hosts")
      .array()
      .notNull()
      .default(sql`'{}'`),
    /**
     * One of `MARKETPLACE_COMPONENT_STATUSES`. Persisted verdict from
     * `classifyMarketplaceComponent(...).verdict`.
     */
    status: text("status").notNull(),
    /** Rule codes that blocked the listing (from Phase 1 classifier output). */
    blockedRules: text("blocked_rules").array().notNull().default(sql`'{}'`),
    /** Rule codes that flagged the listing for review (soft, not blocking). */
    flaggedRules: text("flagged_rules").array().notNull().default(sql`'{}'`),
    /**
     * Free-form classifier reason / admin-note metadata. Bounded by the
     * route layer; schema does not enforce shape so future classifier
     * additions do not require a migration.
     */
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    /** Set when an admin reviewer publishes a `needs_review` listing. */
    approvedAt: timestamp("approved_at", tz),
    approvedBy: uuid("approved_by"),
    /** Set when an admin reviewer rejects (auto-rejected listings also use this). */
    rejectedAt: timestamp("rejected_at", tz),
    rejectedBy: uuid("rejected_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("marketplace_components_tenant_key_uq").on(
      table.tenantId,
      table.listingKey,
    ),
    index("marketplace_components_status_idx").on(table.status),
    index("marketplace_components_tenant_status_idx").on(
      table.tenantId,
      table.status,
    ),
    index("marketplace_components_publisher_idx").on(table.authorPublisher),
  ],
);

export type MarketplaceComponentRow =
  typeof marketplaceComponents.$inferSelect;
export type NewMarketplaceComponentRow =
  typeof marketplaceComponents.$inferInsert;
